import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageContainer, PageHeader, ContentSection } from "../../components/layout";
import { LoadingState, EmptyState } from "../../components/ui";
import { useAuth } from "../../components/context/AuthProvider";
import { UserService, type DashboardStats } from "../../services/userService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faScroll,
  faDragon,
  faUserAlt,
  faCalendarAlt,
  faChevronRight,
  faBolt,
  faHistory,
} from "@fortawesome/free-solid-svg-icons";

// ─── Stat Card ──────────────────────────────────────────────────────────────

interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  primary: string;
  secondary?: string;
  onClick: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ icon, title, primary, secondary, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="group w-full text-left p-6 bg-zinc-900 border border-zinc-800 hover:border-amber-500/50 hover:bg-zinc-800 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
  >
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <span className="text-amber-400 text-xl">{icon}</span>
        <h2 className="text-base font-semibold text-amber-400 tracking-wide">{title}</h2>
      </div>
      <FontAwesomeIcon
        icon={faChevronRight}
        className="text-zinc-600 group-hover:text-amber-400 transition-colors duration-150 mt-1"
      />
    </div>
    <p className="mt-3 text-2xl font-bold text-zinc-100">{primary}</p>
    {secondary && <p className="mt-1 text-sm text-zinc-400">{secondary}</p>}
  </button>
);

// ─── Status Badge ─────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colors: Record<string, string> = {
    active: "bg-emerald-900/60 text-emerald-300 border-emerald-700",
    ended: "bg-zinc-800 text-zinc-400 border-zinc-700",
    planned: "bg-sky-900/60 text-sky-300 border-sky-700",
  };
  const cls = colors[status] ?? "bg-zinc-800 text-zinc-400 border-zinc-700";
  return (
    <span className={`text-xs px-2 py-0.5 rounded border font-medium capitalize ${cls}`}>
      {status}
    </span>
  );
};

// ─── Home ─────────────────────────────────────────────────────────────────────

const Home: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await UserService.getDashboardStats();
        setStats(data);
      } catch (err) {
        setError("Failed to load hearth data. Please try again.");
        console.error("Hearth fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [auth]);

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <PageContainer>
      <PageHeader title="Hearth" />

      {loading ? (
        <ContentSection>
          <LoadingState message="Loading hearth..." size="lg" variant="spinner" />
        </ContentSection>
      ) : error ? (
        <ContentSection>
          <EmptyState title="Error Loading Hearth" description={error} icon="error" size="lg" />
        </ContentSection>
      ) : stats ? (
        <>
          {/* ── Stat Cards ───────────────────────────────────────────────── */}
          <ContentSection>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800">
              <StatCard
                icon={<FontAwesomeIcon icon={faScroll} />}
                title="Entries"
                primary={`${stats.assets.total} total`}
                secondary={
                  stats.assets.recent > 0 ? `+${stats.assets.recent} in the last 7 days` : undefined
                }
                onClick={() => navigate("/protected/assets")}
              />
              <StatCard
                icon={<FontAwesomeIcon icon={faDragon} />}
                title="Chronicles"
                primary={`${stats.campaigns.total} total`}
                secondary={
                  stats.campaigns.active_sessions > 0
                    ? `${stats.campaigns.active_sessions} active session${stats.campaigns.active_sessions !== 1 ? "s" : ""}`
                    : undefined
                }
                onClick={() => navigate("/protected/campaigns")}
              />
              <StatCard
                icon={<FontAwesomeIcon icon={faUserAlt} />}
                title="Heroes"
                primary={`${stats.characters.total} total`}
                onClick={() => navigate("/protected/characters")}
              />
              <StatCard
                icon={<FontAwesomeIcon icon={faCalendarAlt} />}
                title="Sessions"
                primary={`${stats.sessions.total} total`}
                secondary={
                  [
                    stats.sessions.active > 0 ? `${stats.sessions.active} active` : null,
                    stats.sessions.played > 0 ? `${stats.sessions.played} played` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || undefined
                }
                onClick={() => navigate("/protected/campaigns")}
              />
            </div>
          </ContentSection>

          {/* ── Activity Feed ────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 px-6 pb-6">
            {/* Recent Sessions */}
            <ContentSection variant="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-widest flex items-center gap-2">
                  <FontAwesomeIcon icon={faBolt} />
                  Recent Sessions
                </h3>
              </div>
              {stats.recent_sessions.length === 0 ? (
                <p className="text-zinc-500 text-sm">No sessions yet.</p>
              ) : (
                <ul className="divide-y divide-zinc-800">
                  {stats.recent_sessions.map((s) => (
                    <li key={s.id} className="py-3">
                      <button
                        type="button"
                        className="w-full text-left group"
                        onClick={() => navigate(`/protected/campaigns/${s.campaign_id}`)}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-zinc-200 text-sm font-medium group-hover:text-amber-400 transition-colors truncate">
                            {s.campaign_name}
                          </span>
                          <StatusBadge status={s.status} />
                        </div>
                        <p className="text-zinc-500 text-xs mt-0.5">
                          Updated {formatDate(s.updated_at)}
                          {s.theme ? ` · ${s.theme}` : ""}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </ContentSection>

            {/* Recent Campaigns */}
            <ContentSection variant="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-widest flex items-center gap-2">
                  <FontAwesomeIcon icon={faHistory} />
                  Recent Chronicles
                </h3>
              </div>
              {stats.recent_campaigns.length === 0 ? (
                <p className="text-zinc-500 text-sm">No chronicles yet.</p>
              ) : (
                <ul className="divide-y divide-zinc-800">
                  {stats.recent_campaigns.map((c) => (
                    <li key={c.id} className="py-3">
                      <button
                        type="button"
                        className="w-full text-left group"
                        onClick={() => navigate(`/protected/campaigns/${c.id}`)}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-zinc-200 text-sm font-medium group-hover:text-amber-400 transition-colors truncate">
                            {c.name}
                          </span>
                          <span className="text-xs text-zinc-500 shrink-0">{c.type}</span>
                        </div>
                        <p className="text-zinc-500 text-xs mt-0.5">
                          {c.setting} · Updated {formatDate(c.updated_at)}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </ContentSection>
          </div>
        </>
      ) : null}
    </PageContainer>
  );
};

export default Home;
